import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { updateSEOMeta, injectJSONLD } from '@/components/base/SEOHead';
import { categories, categoryMap } from '@/mocks/categories';
import { articleMap, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  useEffect(() => {
    if (article) {
      const catName = categoryMap[article.category]?.name || '';
      updateSEOMeta({
        title: `${article.title} | 抖ying指南`,
        description: article.excerpt,
        keywords: article.tags.join(','),
        canonicalUrl: `https://hanzhu0791.cn/article/${article.slug}`,
        ogType: 'article',
      });

      injectJSONLD({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.date,
        url: `https://hanzhu0791.cn/article/${article.slug}`,
        articleSection: catName,
      }, 'schema-article');
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-32 pb-16 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">该文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const catName = categoryMap[article.category]?.name || '';
  const relatedArticles = getRelatedArticles(article.slug, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: catName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors no-underline whitespace-nowrap"
              >
                {catName}
              </Link>
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-5">
              {article.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/70">
              <span className="flex items-center gap-1.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-user-line text-xs text-accent-600"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-foreground-400"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-foreground-400"></i>
                阅读约{article.readTime}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom mb-12"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags Footer */}
          <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-background-200/70 mb-12">
            <span className="text-sm text-foreground-500 mr-1">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-8">
            <div className="border-t border-background-200/70 pt-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 no-underline flex flex-col"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3 flex-1">
                        {rel.excerpt}
                      </p>
                      <span className="text-xs text-foreground-400">{rel.readTime} · {rel.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back to Home CTA */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页探索更多内容
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}